import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatMoney, formatDateTime, getCurrency } from "@/lib/prefs";
import { UNIT_KINDS } from "@/lib/units";
import { CircleDollarSign, UtensilsCrossed, HandCoins, Trash2, Loader2, Save, ChefHat, GlassWater, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/components/ImageUpload";
import DataExportButton from "@/components/DataExportButton";
import { exportLedgerCsv, exportLedgerPdf, exportLedgerXlsx } from "@/lib/ledgerExport";
import EmptyState from "@/components/EmptyState";

const ALL_UNITS = [...UNIT_KINDS.weight, ...UNIT_KINDS.volume, ...UNIT_KINDS.count];

export default function Ledger() {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [sales, setSales] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [r, i, s, d] = await Promise.all([
      api.get("/recipes"),
      api.get("/ingredients"),
      api.get("/sales?limit=50"),
      api.get("/deductions?limit=50"),
    ]);
    setRecipes(r.data);
    setIngredients(i.data);
    setSales(s.data);
    setDeductions(d.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const recipeById = useMemo(() => Object.fromEntries(recipes.map((r) => [r.id, r])), [recipes]);
  const ingredientById = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const exportPayload = () => ({ sales, deductions, recipeById, ingredientById, currency: getCurrency() });
  const handleCsvExport = () => {
    exportLedgerCsv(exportPayload());
    toast.success(`Ledger CSV: ${sales.length + deductions.length} row${sales.length + deductions.length === 1 ? "" : "s"} exported`);
  };
  const handleXlsxExport = () => {
    exportLedgerXlsx(exportPayload());
    toast.success(`Ledger Excel: ${sales.length + deductions.length} row${sales.length + deductions.length === 1 ? "" : "s"} exported`);
  };
  const handlePdfExport = () => exportLedgerPdf(exportPayload());

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="ledger-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><CircleDollarSign className="w-3 h-3" /> Ledger</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Sales & Deductions</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Track daily sales, log operational waste, and audit deduction lines. Inventory, cost of goods, and the owner's report update in real time.
          </p>
        </div>
        <DataExportButton
          onCsv={handleCsvExport}
          onXlsx={handleXlsxExport}
          onPdf={handlePdfExport}
          label="Export"
          testId="ledger-export"
          disabled={loading}
        />
      </header>

      <Tabs defaultValue="sales" className="w-full">
        <TabsList data-testid="ledger-tabs">
          <TabsTrigger value="sales" className="gap-2" data-testid="tab-sales"><CircleDollarSign className="w-4 h-4" /> Daily Sales</TabsTrigger>
          <TabsTrigger value="staff" className="gap-2" data-testid="tab-staff-meal"><UtensilsCrossed className="w-4 h-4" /> Staff Meals</TabsTrigger>
          <TabsTrigger value="comp" className="gap-2" data-testid="tab-comp"><HandCoins className="w-4 h-4" /> Comps</TabsTrigger>
          <TabsTrigger value="spillage" className="gap-2" data-testid="tab-spillage"><Trash2 className="w-4 h-4" /> Spillage</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-6">
          <DailySalesForm recipes={recipes} onSaved={load} />
        </TabsContent>
        <TabsContent value="staff" className="mt-6">
          <RecipeDeductionForm kind="staff_meal" recipes={recipes} onSaved={load} label="Staff meal" />
        </TabsContent>
        <TabsContent value="comp" className="mt-6">
          <RecipeDeductionForm kind="comp" recipes={recipes} onSaved={load} label="Comp" />
        </TabsContent>
        <TabsContent value="spillage" className="mt-6">
          <SpillageForm ingredients={ingredients} onSaved={load} />
        </TabsContent>
      </Tabs>

      <RecentActivity sales={sales} deductions={deductions} loading={loading} onDeleted={load} />
    </div>
  );
}

// ---------- Daily Sales (batch) ----------
function DailySalesForm({ recipes, onSaved }) {
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const total = Object.entries(drafts).reduce((s, [id, q]) => {
    const r = recipes.find((x) => x.id === id);
    return s + (Number(q) || 0) * (r?.sell_price || 0);
  }, 0);
  const changed = Object.entries(drafts).filter(([_, q]) => Number(q) > 0);

  const submit = async () => {
    if (!changed.length) return;
    setSaving(true);
    let ok = 0;
    for (const [id, q] of changed) {
      try { await api.post("/sales", { recipe_id: id, quantity_sold: Number(q) }); ok += 1; }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    }
    toast.success(`Recorded ${ok} line${ok === 1 ? "" : "s"}`);
    setDrafts({});
    onSaved?.();
    setSaving(false);
  };

  const grouped = { food: [], drink: [] };
  recipes.forEach((r) => grouped[r.bracket || "food"].push(r));

  return (
    <div className="space-y-4" data-testid="daily-sales-form">
      <div className="rounded-2xl glass-card overflow-hidden">
        {["food", "drink"].map((k) => grouped[k].length ? (
          <div key={k}>
            <div className="px-5 py-2 label-eyebrow border-b border-border flex items-center gap-2">
              {k === "food" ? <ChefHat className="w-3 h-3" /> : <GlassWater className="w-3 h-3" />} {k}
            </div>
            <div className="divide-y divide-border/60">
              {grouped[k].map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3" data-testid={`sales-row-${r.id}`}>
                  {r.image_url ? (
                    <img src={resolveImageUrl(r.image_url)} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-secondary" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{formatMoney(r.sell_price)} • sell</div>
                  </div>
                  <Input
                    type="number" min="0" step="1"
                    value={drafts[r.id] ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                    placeholder="0"
                    className="w-20 text-right tabular"
                    data-testid={`sales-qty-${r.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null)}
      </div>
      <div className="flex items-center justify-between gap-3 rounded-2xl glass-card p-4">
        <div>
          <div className="label-eyebrow text-[0.55rem]">Total sale</div>
          <div className="font-display text-3xl tabular gold" data-testid="daily-sales-total">{formatMoney(total)}</div>
        </div>
        <Button onClick={submit} disabled={saving || !changed.length} className="gap-2" data-testid="daily-sales-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Record {changed.length} line{changed.length === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}

// ---------- Recipe-based deduction (staff meal / comp) ----------
function RecipeDeductionForm({ kind, recipes, onSaved, label }) {
  const [form, setForm] = useState({ recipe_id: "", quantity: 1, notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.recipe_id) return toast.error("Pick a recipe");
    setSaving(true);
    try {
      await api.post("/deductions", { kind, recipe_id: form.recipe_id, quantity: Number(form.quantity), notes: form.notes });
      toast.success(`${label} recorded`);
      setForm({ recipe_id: "", quantity: 1, notes: "" });
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl glass-card p-6 space-y-4 max-w-xl" data-testid={`deduction-form-${kind}`}>
      <div>
        <Label>Recipe</Label>
        <Select value={form.recipe_id} onValueChange={(v) => setForm({ ...form, recipe_id: v })}>
          <SelectTrigger data-testid={`${kind}-recipe`}><SelectValue placeholder="Select a recipe…" /></SelectTrigger>
          <SelectContent>
            {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Quantity</Label>
        <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid={`${kind}-qty`} />
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`${kind}-notes`} placeholder={kind === "comp" ? "e.g. Manager comp: birthday" : "e.g. Family meal"} />
      </div>
      <Button onClick={submit} disabled={saving} className="gap-2" data-testid={`${kind}-submit`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Record {label}
      </Button>
    </div>
  );
}

// ---------- Spillage (ingredient-level) ----------
function SpillageForm({ ingredients, onSaved }) {
  const [form, setForm] = useState({ ingredient_id: "", quantity: "", unit: "g", notes: "" });
  const [saving, setSaving] = useState(false);
  const ing = useMemo(() => ingredients.find((i) => i.id === form.ingredient_id), [ingredients, form.ingredient_id]);

  useEffect(() => { if (ing) setForm((f) => ({ ...f, unit: ing.purchase_unit_kind === "count" ? "each" : ing.purchase_unit })); }, [ing]);

  const submit = async () => {
    if (!form.ingredient_id || !form.quantity) return toast.error("Pick an ingredient + qty");
    setSaving(true);
    try {
      await api.post("/deductions", { kind: "spillage", ingredient_id: form.ingredient_id, quantity: Number(form.quantity), unit: form.unit, notes: form.notes });
      toast.success("Spillage recorded");
      setForm({ ingredient_id: "", quantity: "", unit: "g", notes: "" });
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl glass-card p-6 space-y-4 max-w-xl" data-testid="spillage-form">
      <div>
        <Label>Ingredient</Label>
        <Select value={form.ingredient_id} onValueChange={(v) => setForm({ ...form, ingredient_id: v })}>
          <SelectTrigger data-testid="spillage-ingredient"><SelectValue placeholder="Select an ingredient…" /></SelectTrigger>
          <SelectContent>
            {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Quantity</Label>
          <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="spillage-qty" />
        </div>
        <div>
          <Label>Unit</Label>
          <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
            <SelectTrigger data-testid="spillage-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALL_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="spillage-notes" placeholder="e.g. Dropped case, freezer burn" />
      </div>
      <Button onClick={submit} disabled={saving} className="gap-2" data-testid="spillage-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Record spillage
      </Button>
    </div>
  );
}

// ---------- Recent activity ----------
function RecentActivity({ sales, deductions, loading, onDeleted }) {
  const merged = [
    ...sales.map((s) => ({ ...s, _type: "sale", when: s.sold_at, label: `Sale • ${s.recipe_name} × ${s.quantity_sold}`, value: s.revenue })),
    ...deductions.map((d) => ({ ...d, _type: d.kind, when: d.created_at, label: `${d.kind.replace("_", " ")} • ${d.recipe_name || d.ingredient_name} × ${d.quantity}${d.unit && d.ingredient_id ? " " + d.unit : ""}`, value: -(d.food_cost || 0) })),
  ].sort((a, b) => new Date(b.when) - new Date(a.when)).slice(0, 40);

  const kindColor = {
    sale: "gold",
    staff_meal: "text-muted-foreground",
    comp: "text-destructive",
    spillage: "text-destructive",
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this entry? Inventory will NOT be restored automatically.")) return;
    if (row._type === "sale") await api.delete(`/sales/${row.id}`);
    else await api.delete(`/deductions/${row.id}`);
    toast.success("Deleted");
    onDeleted?.();
  };

  return (
    <div>
      <div className="label-eyebrow mb-3">Recent activity</div>
      {loading ? (
        <div className="rounded-2xl glass-card p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : !merged.length ? (
        <EmptyState
          illustration="ledger"
          eyebrow="Ledger"
          title="No activity in the ledger yet"
          body="Every sale, staff meal, comp, and spillage lands here. Log the first entry to begin the audit trail."
        />
      ) : (
        <div className="rounded-2xl glass-card overflow-hidden">
          <ul className="divide-y divide-border/60">
            {merged.map((row) => (
              <li key={`${row._type}-${row.id}`} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`ledger-${row._type}-${row.id}`}>
                <div className="flex-1 min-w-0">
                  <div className={`truncate ${kindColor[row._type] || ""}`}>{row.label}</div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(row.when)}{row.notes ? ` • ${row.notes}` : ""}</div>
                </div>
                <div className={`tabular font-medium ${row.value >= 0 ? "gold" : "text-destructive"}`}>{formatMoney(row.value)}</div>
                <Button size="sm" variant="ghost" onClick={() => remove(row)} data-testid={`ledger-delete-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
